import { useState, useCallback, useRef, useEffect } from "react";
import host from "../../../../../../global";

/**
 * Manages the round configuration state for the Custom JD flow.
 * Owns: round object, roundConfigs lookup, duration debounce, and round handlers.
 *
 * roundConfigs shape: { [roundId]: { focus: string, focusArray: string[] } }
 */
export function useRoundState({ assessmentId, accessToken }) {
  const durationDebounceRef = useRef(null);
  const roundRef = useRef(null); // stable ref to avoid stale closure in debounced persist

  const [round, setRound] = useState({
    id: null,
    type: "Technical Interview",
    duration: 45,
    focus: "",
    title: "Round 1",
    status: "setup",
    requirements: [],
    roundRequirements: null,
  });

  const [roundConfigs, setRoundConfigs] = useState({}); // { [roundId]: { focus, focusArray } }

  // Keep ref in sync so debounced callback always reads the latest round
  roundRef.current = round;

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (durationDebounceRef.current) clearTimeout(durationDebounceRef.current);
    };
  }, []);

  const handleDurationChange = useCallback(
    (duration) => {
      setRound((prev) => ({ ...prev, duration }));

      // Debounced persist — reads roundRef to avoid stale closure
      if (durationDebounceRef.current) clearTimeout(durationDebounceRef.current);
      durationDebounceRef.current = setTimeout(async () => {
        const { id } = roundRef.current;
        if (id && assessmentId) {
          try {
            await fetch(`${host}/api/assessments/${assessmentId}/rounds/${id}`, {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
              credentials: "include",
              body: JSON.stringify({ duration }),
            });
          } catch (_) {}
        }
      }, 500);
    },
    [assessmentId, accessToken]
  );

  const handleFocusChange = useCallback(
    (focusString, focusArray) => {
      if (round.id) {
        setRoundConfigs((prev) => ({
          ...prev,
          [round.id]: { focus: focusString, focusArray: focusArray || [] },
        }));
      }
    },
    [round.id]
  );

  return {
    round,
    setRound,
    roundConfigs,
    setRoundConfigs,
    handleDurationChange,
    handleFocusChange,
  };
}
